import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useLocation } from "wouter";
import CommonGrid from "@/components/pos/admin/common/grid";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import { useDebounce } from "@/hooks/pos/use-debounce";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";

interface ModifierListProps {
  groupId: string;
  onBack: () => void;
}

export default function ModifierList({ groupId, onBack }: ModifierListProps) {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery.trim(), 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: `modifiers_table_${groupId}`,
    defaultColumn: "displayOrder",
    defaultDirection: "asc",
  });

  // --- Fetch modifier group info ---

  const { data: modifierGroup, isLoading: groupLoading } = useQuery<any>({
    queryKey: ["/v1/pos/modifier-groups", groupId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/modifier-groups/${groupId}`);
      if (!res.ok) throw new Error("Failed to fetch modifier group");
      return res.json();
    },
    enabled: !!groupId,
  });

  // --- Fetch modifiers ---

  const { data: modifiers = [], isLoading: modifiersLoading } = useQuery<any[]>({
    queryKey: ["/v1/pos/modifier-groups", groupId, "modifiers"],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/modifier-groups/${groupId}/modifiers`);
      if (!res.ok) throw new Error("Failed to fetch modifiers");
      return res.json();
    },
    enabled: !!groupId,
    retry: 3,
    retryDelay: 1000,
  });

  useEffect(() => {
    document.title = `Modifiers${modifierGroup?.displayName ? ` - ${modifierGroup.displayName}` : ""} | SECH Admin`;
  }, [modifierGroup]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await posApiFetch(`/v1/pos/modifiers/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete modifier");
      toast({ title: "Modifier deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/modifier-groups", groupId, "modifiers"] });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/modifier-groups"] });
    } catch {
      toast({ title: "Error", description: "Failed to delete modifier", variant: "destructive" });
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const setTableColumns = useMemo(() => [
    {
      label: "Name", field: "displayName", sortable: true,
      cellTemplate: (value: string, row: any) => (
        <div className="flex items-center gap-2">
          <NameAvatar name={value || row?.name || ""} />
          <div className="flex flex-col">
            <span className="text-[13px] text-[#505258]">{value || "-"}</span>
            {row?.name && row.name !== value && (
              <span className="text-[11px] text-[var(--secondary-text-color)]">{row.name}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      label: "Price Adj.", field: "priceAdjustmentUsd", sortable: true,
      cellTemplate: (value: string) => {
        const price = parseFloat(value || "0");
        const formatted = price === 0 ? "—" : price > 0 ? `+$${price.toFixed(2)}` : `-$${Math.abs(price).toFixed(2)}`;
        const colorClass = price > 0 ? "text-green-600" : price < 0 ? "text-red-600" : "text-[var(--secondary-text-color)]";
        return <span className={`text-[13px] font-medium ${colorClass}`}>{formatted}</span>;
      },
    },
    {
      label: "Default", field: "isDefault", sortable: true,
      cellTemplate: (value: boolean) => (
        <span className={`text-[13px] ${value ? "text-green-600 font-medium" : "text-[var(--secondary-text-color)]"}`}>
          {value ? "Yes" : "No"}
        </span>
      ),
    },
    {
      label: "Status", field: "active", sortable: true,
      cellTemplate: (value: any) => {
        const active = value === true || value === "true";
        const status = active ? "active" : "inactive";
        return (
          <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColorCode(status)}`}>
            <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(status)} inline-flex items-center justify-center`} />
            <span className="leading-5 text-[13px] font-normal">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </span>
        );
      },
    },
    {
      label: "Order", field: "displayOrder", sortable: true,
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ?? "-"}</span>,
    },
    {
      label: "Date & Time", field: "createdAt", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: any) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: any) => navigate(`/admin/modifier-groups/${groupId}/modifiers/${r.id}/edit`) },
            { label: "Delete", icon: Trash2, onClick: (r: any) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], [groupId]);

  const filteredModifiers = useMemo(() =>
    modifiers.filter((mod: any) => {
      const search = debouncedSearch.toLowerCase();
      if (!search) return true;
      return mod.name?.toLowerCase().includes(search) ||
        mod.displayName?.toLowerCase().includes(search);
    }), [modifiers, debouncedSearch]
  );

  const sortedModifiers = useMemo(() => {
    return [...filteredModifiers].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "displayName": aVal = a?.displayName ?? ""; bVal = b?.displayName ?? ""; break;
        case "priceAdjustmentUsd": aVal = parseFloat(a?.priceAdjustmentUsd ?? "0"); bVal = parseFloat(b?.priceAdjustmentUsd ?? "0"); break;
        case "isDefault": aVal = a?.isDefault ? 1 : 0; bVal = b?.isDefault ? 1 : 0; break;
        case "active": aVal = a?.active ? 1 : 0; bVal = b?.active ? 1 : 0; break;
        case "displayOrder": aVal = a?.displayOrder ?? 0; bVal = b?.displayOrder ?? 0; break;
        case "createdAt": aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0; break;
        default: aVal = a?.displayOrder ?? 0; bVal = b?.displayOrder ?? 0;
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredModifiers, sortColumn, sortDirection]);

  const pagination = {
    totalPages: 1,
    currentPage: 1,
    itemsPerPage: sortedModifiers.length || 10,
    totalItems: sortedModifiers.length,
    handlePageChange: () => {},
    handleItemsPerPageChange: () => {},
  };

  if (groupLoading) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Modifier Groups
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1 px-2">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <p className="page-heading text-[20px] leading-[30px] font-semibold">
            {modifierGroup?.displayName ?? "Modifiers"}
          </p>
        </div>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input
              className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center"
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            onClick={() => navigate(`/admin/modifier-groups/${groupId}/modifiers/add`)}
            className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1"
          >
            <Plus className="h-4 w-4" /> Add Modifier
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="pt-0 h-[calc(100vh-130px)]">
          {!sortedModifiers.length && !modifiersLoading ? (
            <NoRecordFound
              title="Modifiers"
              description={`No modifiers found for "${modifierGroup?.displayName ?? "this group"}". Add modifier options like "Rare", "Medium", "Well Done".`}
              action={
                <Button
                  onClick={() => navigate(`/admin/modifier-groups/${groupId}/modifiers/add`)}
                  className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" /> Add Modifier
                </Button>
              }
            />
          ) : (
            <CommonGrid
              rowData={sortedModifiers}
              isLoading={modifiersLoading}
              columns={setTableColumns}
              pagination={pagination}
              isPaginationRequired={false}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
      <ConfirmModal
        open={deleteModal}
        onClose={() => { setDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title="Delete Modifier"
        description={`Are you sure you want to delete "${deleteTarget?.displayName || deleteTarget?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="delete"
      />
    </div>
  );
}
